import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import moment from 'moment';

import MuiTypography from '@material-ui/core/Typography';
import MuiCheckCircleIcon from '@material-ui/icons/CheckCircle';
import MuiCircle from '@material-ui/icons/FiberManualRecord';
import { withStyles } from '@material-ui/core/styles';

import Button from 'Components/UI/Button';
import SequenceBarItem from 'Components/UI/SequenceBarItem';
import StyledTooltip from 'Components/UI/StyledTooltip';
import Chip from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Chip';
import Grow from '@mui/material/Grow';
import IconButton from '@material-ui/core/IconButton';
import CreateIcon from '@material-ui/icons/Create';
import Paper from '@material-ui/core/Paper';
import MetaFormInline from 'Components/UI/Meta/MetaForm/Components/MetaFormInline';
import useResource from 'Hooks/useResource';
import { dispatchMeetingContextRefresh, useRefreshMeetingContext } from 'Hooks/useRefreshEvent';
import { pickComponentAndProps } from 'Helpers/Affordances';
import relationships from 'Consts/Relationships';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';
import EventHoverLayout from 'Components/AdvisorApp/Viewport/MissionControl/Dashboard/SequenceBar/EventHoverLayout';
import { EditMeetingModalOverride } from 'Resources/Meeting';
import { removeQuery } from 'Helpers/Query';

const styles = theme => ({
  root: {
    width: '100%',
  },
  footerText: {
    fontWeight: theme.typography.fontWeightBold,
  },
  headerText: {
    color: theme.palette.facet.primaryGray,
    fontWeight: theme.typography.fontWeightBold,
  },
  futureMeetingText: {
    color: theme.palette.primary.main,
  },
  selectedEventText: {
    color: theme.palette.facet.accentBlue2,
    '&:before': {
      content: '""',
      position: 'absolute',
      display: 'block',
      bottom: theme.spacing(-1 * 0.5),
      left: 0,
      width: '100%',
      height: theme.spacing(0.5),
      backgroundColor: theme.palette.facet.accentBlue2,
      borderRadius: theme.radius.xs,
    },
  },
  completedMeetingText: {
    color: theme.palette.secondary.main,
  },
  disabledText: {
    color: theme.palette.text.disabled,
  },
  tooltip: {
    borderRadius: theme.radius.sm,
    fontSize: theme.typography.h6.fontSize,
    padding: theme.spacing(0.5, 2),
    maxWidth: '400px',
  },
  tooltipOpen: {
    padding: theme.spacing(0.5),
  },
  tooltipPopper: {
    minWidth: theme.dimensions.sm,
    zIndex: 100, // Rather than 1500 for a normal real tooltip
  },
  formTitle: {
    color: theme.palette.facet.blueGray2,
    padding: theme.spacing(1),
    borderBottom: '1px solid lightgrey',
    textAlign: 'center',
  },
  closedTitle: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
  },
  closedTitleText: {
    flex: 10,
    flexBasis: '10%',
  },
  editIcon: {
    flex: 1,
    color: theme.palette.common.white,
    padding: theme.spacing(1),
  },
  formPaper: {
    borderRadius: theme.radius.xs,
  },
  menuIcon: {
    backgroundColor: theme.palette.facet.accentBlue,
    color: theme.palette.common.white,
    padding: theme.spacing(0.5),
    '&:hover': {
      backgroundColor: theme.palette.facet.accentBlue,
      color: theme.palette.common.white,
      padding: theme.spacing(0.5),
    },
  },
  chipContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    paddingTop: theme.spacing(0.5),
  },
  chip: {
    margin: theme.spacing(0.25),
  },
});

const iconMap = {
  completedMeeting: MuiCheckCircleIcon,
  futureMeeting: MuiCircle,
  selectedMeeting: MuiCircle,
};

function SequenceBarEvent({
  className,
  classes,
  disabled,
  eventResource,
  isNextEvent,
  variant,
  IconButtonProps,
  IconProps,
  displayEditButton,
  ...rest
}) {
  const { logEvent } = useEventTrackingLogEvent();

  const Icon = iconMap[variant];

  if (Icon === undefined) {
    throw new Error(`Icon not found in iconMap with variant: ${variant}`);
  }

  const footerTextClass = classNames(classes.footerText, {
    [classes.completedMeetingText]: variant === 'completedMeeting',
    [classes.futureMeetingText]: variant === 'futureMeeting',
    [classes.selectedEventText]: !disabled && variant === 'selectedMeeting',
    [classes.disabledText]: disabled,
  });

  const [tooltipOpen, setTooltipOpen] = useState(false);
  const { resource, setAttribute } = useResource(eventResource);
  const [bnsPrompts, setBnsPrompts] = useState([]);

  function updateBNSPrompts() {
    if (eventResource?.attr('bestNextSteps')) {
      let keyCounter = 0;
      setBnsPrompts(eventResource.attr('bestNextSteps').resources().list().map((bestNextStep) => {
        return {
          key: keyCounter++,
          value: bestNextStep.attr('bestNextStepName'),
        };
      }));
    }
  }

  useEffect(() => {
    updateBNSPrompts();
  }, [eventResource]);

  useRefreshMeetingContext(() => {
    updateBNSPrompts();
  }, [eventResource]);

  const BNSChips = () => (
    <div className={classes.chipContainer}>
      <>
        {bnsPrompts.map(prompt => (<Chip
          className={classes.chip}
          key={prompt.key}
          size="small"
          label={prompt.value}
        />))}
      </>
    </div>
  );

  const { component: BNSComponent, props: bnsComponentProps } = pickComponentAndProps(eventResource, {
    [relationships.bestNextSteps]: [BNSChips, {}],
    [relationships.default]: null,
  });

  let iconColor;
  switch (variant) {
    case 'completedMeeting':
      iconColor = 'secondary';
      break;
    case 'futureMeeting':
    case 'selectedMeeting':
      iconColor = 'accentBlue2';
      break;
    default:
      throw new Error(`iconColor found for variant: ${variant}`);
  }

  const eventTypePrompt = resource
    .meta()
    .field('meetingType')
    .selectedOption()
    .attr('prompt');
  const eventStartTime = moment(resource.attr('startTime')).format('l');

  const title = [
    eventTypePrompt,
    moment(resource.attr('startTime')).format('l'),
    moment(resource.attr('startTime')).format('LT'),
  ].join(' - ');

  const closedTitle = (
    <span
      className={classes.closedTitle}
      onClick={(e) => {
        logEvent('Clicked Mission Control Sequence Bar Event');
        e.preventDefault();
        e.stopPropagation();
      }}
      role="presentation"
    >
      <span className={classes.closedTitleText}>
        {title}
      </span>
      {displayEditButton && (
      <IconButton
        size="small"
        className={classes.editIcon}
        onClick={() => setTooltipOpen(true)}
      >
        <CreateIcon />
      </IconButton>)
      }
    </span>
  );

  const MeetingResourceOverride = EditMeetingModalOverride;

  const resourceWithOverride = resource.overrideMeta(MeetingResourceOverride);

  const openTitle = (
    <Grow in>
      <div onClick={e => e.preventDefault()} role="presentation">
        <Paper
          variant="outlined"
          className={classes.formPaper}
          square
        >
          <MuiTypography
            className={classes.formTitle}
          >
            {title}
          </MuiTypography>
          <MetaFormInline
            resource={resourceWithOverride}
            layout={EventHoverLayout}
            onCancel={() => {
              setTooltipOpen(false);
              removeQuery('nextOpen');
            }}
            onSubmit={(resp) => {
              setAttribute('meetingType', resp.resources().head().attr('meetingType'));
              setTooltipOpen(false);
              removeQuery('nextOpen');

              // If this is the selected meeting then please refresh it
              if (variant === 'selectedMeeting') {
                dispatchMeetingContextRefresh();
              }
            }}
          />
        </Paper>
        {BNSComponent && <BNSComponent {...bnsComponentProps} />}
      </div>
    </Grow>);

  const nextEvent = (
    <Button
      color={iconColor}
      disabled={disabled || tooltipOpen}
      variant="icon"
      className={classNames()}
      {...IconButtonProps}
      onClick={(e) => {
        logEvent('Clicked Mission Control Sequence Bar Next Event');
        if (tooltipOpen && isNextEvent) {
          e.preventDefault();
        }
      }}
    >
      <StyledTooltip
        arrow
        interactive
        PopperProps={{ disablePortal: true }}
        classes={{
          popper: classes.tooltipPopper,
          tooltip: tooltipOpen ? classNames(classes.tooltip, classes.tooltipOpen) : classes.tooltip,
        }}
        open
        title={tooltipOpen ? openTitle : closedTitle}
        color="accentBlue2"
      >
        <Icon
          {...IconProps}
        />
      </StyledTooltip>
    </Button>
  );

  const eventText = `${eventTypePrompt}\n${eventStartTime}`;

  return (
    <SequenceBarItem
      className={classNames(classes.root, className)}
      footer={
        !isNextEvent && (
          <MuiTypography
            className={footerTextClass}
            test={`mission-control-sequence-bar eventType:${eventTypePrompt} eventStartTime:${eventStartTime}`}
          >
            {eventText}
          </MuiTypography>
        )
      }
      header={
        isNextEvent && (
          <MuiTypography
            className={classes.headerText}
            variant="h6"
            test="mission-control-sequence-bar-upcoming-event-title"
          >
            {'Next Event'}
          </MuiTypography>
        )
      }
      {...rest}
    >
      {isNextEvent ? nextEvent : (
        <div
          onMouseEnter={() => !disabled && setTooltipOpen(true)}
          onMouseLeave={() => !disabled && setTooltipOpen(false)}
        >
          <Button
            color={iconColor}
            disabled={disabled}
            variant="icon"
            {...IconButtonProps}
          >
            {tooltipOpen
              ? (
                <StyledTooltip
                  arrow
                  interactive
                  classes={{
                    popper: classes.tooltipPopper,
                    tooltip: classNames(classes.tooltip, classes.tooltipOpen),
                  }}
                  open
                  title={openTitle}
                  color="accentBlue2"
                >
                  <Icon {...IconProps} />
                </StyledTooltip>
              ) : (
                <Icon {...IconProps} />
              )}
          </Button>
        </div>
      )}
    </SequenceBarItem>
  );
}

SequenceBarEvent.defaultProps = {
  className: '',
  isNextEvent: false,
  IconButtonProps: {},
  IconProps: {},
  displayEditButton: false,
};

SequenceBarEvent.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  eventResource: PropTypes.object.isRequired,
  isNextEvent: PropTypes.bool,
  variant: PropTypes.oneOf([
    'completedMeeting',
    'futureMeeting',
    'selectedMeeting',
  ]).isRequired,
  IconButtonProps: PropTypes.object,
  IconProps: PropTypes.object,
  displayEditButton: PropTypes.bool,
};

export default withStyles(styles, { name: 'SequenceBarEvent' })(
  SequenceBarEvent,
);
